import type {
  AgentEdge,
  AgentNode,
  AgentNodeData,
} from '@saaga/shared/interfaces/agency-node.interface'
import type { Connection, EdgeChange, NodeChange } from '@xyflow/react'
import { useCallback } from 'react'

import { canDeleteNode, type DeleteNodeResult } from './agency-flow-delete.util'
import { getLayoutedElements } from './agency-flow-layout.util'
import { useAgencyFlowEdges } from './use-agency-flow-edges.hook'
import { useAgencyFlowNodes } from './use-agency-flow-nodes.hook'
import { useAgencyFlowSelection } from './use-agency-flow-selection.hook'

interface UseAgencyFlowReturn {
  nodeList: AgentNode[]
  edgeList: AgentEdge[]
  selectedNodeId: string | null
  selectedNode: AgentNode | null
  selectedEdgeId: string | null
  selectedEdge: AgentEdge | null
  onNodesChange: (changes: NodeChange[]) => void
  onEdgesChange: (changes: EdgeChange[]) => void
  onConnect: (connection: Connection) => void
  onNodeClick: (nodeId: string) => void
  onEdgeClick: (edgeId: string) => void
  addNode: () => void
  addNodeWithConnection: (position: { x: number; y: number }, sourceNodeId: string) => void
  deleteNode: (nodeId: string) => DeleteNodeResult
  updateNodeData: (nodeId: string, data: Partial<AgentNodeData>) => void
  updateEdgeLabel: (edgeId: string, label: string) => void
  clearSelection: () => void
  applyLayout: () => void
}

export const useAgencyFlow = (
  initialNodeList?: AgentNode[],
  initialEdgeList?: AgentEdge[]
): UseAgencyFlowReturn => {
  const nodes = useAgencyFlowNodes(initialNodeList)
  const edges = useAgencyFlowEdges(initialEdgeList)
  const selection = useAgencyFlowSelection(nodes.nodeList, edges.edgeList)

  const addNode = useCallback(() => nodes.addNode(edges.edgeList), [nodes, edges.edgeList])

  const addNodeWithConnection = useCallback(
    (position: { x: number; y: number }, sourceNodeId: string) =>
      nodes.addNodeWithConnection(position, sourceNodeId, edges.addEdgeToList),
    [nodes, edges]
  )

  const deleteNode = useCallback(
    (nodeId: string): DeleteNodeResult => {
      const result = canDeleteNode(nodeId, nodes.nodeList, edges.edgeList)
      if (!result.success) return result

      nodes.setNodeList((prev) => prev.filter((n) => n.id !== nodeId))
      edges.removeEdgesForNode(nodeId)
      if (selection.selectedNodeId === nodeId) selection.setSelectedNodeId(null)

      return result
    },
    [nodes, edges, selection]
  )

  const applyLayout = useCallback(() => {
    const { nodeList: ln, edgeList: le } = getLayoutedElements(nodes.nodeList, edges.edgeList)
    nodes.setNodeList(ln)
    edges.setEdgeList(le)
  }, [nodes, edges])

  return {
    nodeList: nodes.nodeList,
    edgeList: edges.edgeList,
    selectedNodeId: selection.selectedNodeId,
    selectedNode: selection.selectedNode,
    selectedEdgeId: selection.selectedEdgeId,
    selectedEdge: selection.selectedEdge,
    onNodesChange: nodes.onNodesChange,
    onEdgesChange: edges.onEdgesChange,
    onConnect: edges.onConnect,
    onNodeClick: selection.onNodeClick,
    onEdgeClick: selection.onEdgeClick,
    addNode,
    addNodeWithConnection,
    deleteNode,
    updateNodeData: nodes.updateNodeData,
    updateEdgeLabel: edges.updateEdgeLabel,
    clearSelection: selection.clearSelection,
    applyLayout,
  }
}
